import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    Plus, Search, ChevronRight, Sparkles, ChevronLeft,
    Trash2, Check, X, BookOpen, Info, Edit2,
    Layers, Zap, HelpCircle, BrainCircuit
} from 'lucide-react';
import apiClient from '../utils/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import PageHeader from '../components/PageHeader';
import { useToast } from '../context/ToastContext';

const KnowledgeBase = ({ activeAgentId }) => {
    const { showToast } = useToast();
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('questions'); // 'questions' or 'recommendations'
    const [currentPage, setCurrentPage] = useState(1);
    const QUESTIONS_PER_PAGE = 10;

    // New Question / Generate
    const [newQuestion, setNewQuestion] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [recommendedVariations, setRecommendedVariations] = useState([]);

    // Manual Response Form State
    const [showManualResForm, setShowManualResForm] = useState(null); // questionId if showing form
    const [manualInstruction, setManualInstruction] = useState('');
    const [manualTrigger, setManualTrigger] = useState('');
    const [isImprovingVariation, setIsImprovingVariation] = useState(null); // variantIndex or id if improving

    // Editing State
    const [editingQuestionId, setEditingQuestionId] = useState(null);
    const [editingQuestionText, setEditingQuestionText] = useState('');

    const [editingResponseId, setEditingResponseId] = useState(null);
    const [editingInstruction, setEditingInstruction] = useState('');
    const [editingTrigger, setEditingTrigger] = useState('');

    // New Topic Modal
    const [showTopicModal, setShowTopicModal] = useState(false);
    const [topicName, setTopicName] = useState('');
    const [topicDesc, setTopicDesc] = useState('');

    const [expandedQuestions, setExpandedQuestions] = useState([]);

    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteData, setDeleteData] = useState({ id: null, type: null, extraId: null });
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchTopics = React.useCallback(async () => {
        try {
            const data = await apiClient.getKBTopics();
            setTopics(data);
            if (data.length > 0 && !selectedTopic) {
                setSelectedTopic(data[0]);
            }
        } catch (err) {
            console.error("Failed to fetch topics", err);
        }
    }, [selectedTopic]);

    const fetchQuestions = async (topicId) => {
        setLoading(true);
        try {
            const data = await apiClient.getKBQuestions(topicId);
            setQuestions(data);
        } catch (err) {
            console.error("Failed to fetch questions", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTopics();
        setSelectedTopic(null); // Reset selected topic on agent switch
    }, [activeAgentId, fetchTopics]);

    useEffect(() => {
        if (selectedTopic) {
            fetchQuestions(selectedTopic.id);
        }
    }, [selectedTopic]);

    const handleCreateTopic = async () => {
        if (!topicName.trim()) return;
        try {
            await apiClient.createKBTopic(topicName, topicDesc);
            setTopicName('');
            setTopicDesc('');
            setShowTopicModal(false);
            fetchTopics();
        } catch (err) {
            console.error("Failed to create topic", err);
        }
    };

    const handleDeleteTopic = (e, topicId) => {
        e.stopPropagation();
        setDeleteData({ id: topicId, type: 'topic' });
        setIsDeleteModalOpen(true);
    };

    const handleGenerateVariations = async () => {
        if (!newQuestion.trim() || !selectedTopic) return;
        setIsGenerating(true);
        setActiveTab('recommendations');
        try {
            const variations = await apiClient.generateFAQVariations(selectedTopic.name, newQuestion);
            // AI generates 'Question Variations'
            setRecommendedVariations(variations.map(qText => ({
                question_text: qText,
                instruction_text: '',
                trigger_condition: ''
            })));
        } catch (err) {
            console.error("Failed to generate variations", err);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleUpdateVariationInput = (index, field, value) => {
        setRecommendedVariations(prev => {
            const newArr = [...prev];
            newArr[index] = { ...newArr[index], [field]: value };
            return newArr;
        });
    };

    const handleApproveVariation = async (variation, index) => {
        if (!variation.instruction_text.trim() || !variation.trigger_condition.trim()) {
            showToast("Please provide both Instruction and Trigger Condition before approving.", "warning");
            return;
        }

        try {
            // Since AI generated a new way to ask, we create a new question entry for it
            const qRes = await apiClient.createKBQuestion(selectedTopic.id, variation.question_text);
            const questionId = qRes.id;

            await apiClient.createKBResponse(
                questionId,
                variation.instruction_text,
                variation.trigger_condition
            );

            // Remove from recommendations after approval
            setRecommendedVariations(prev => prev.filter((_, i) => i !== index));
            fetchQuestions(selectedTopic.id);
        } catch (err) {
            console.error("Failed to approve variation", err);
        }
    };

    const handleAddManualResponse = async (questionId) => {
        if (!manualInstruction.trim() || !manualTrigger.trim()) {
            showToast("Please fill in both fields.", "warning");
            return;
        }

        try {
            await apiClient.createKBResponse(questionId, manualInstruction, manualTrigger);
            setManualInstruction('');
            setManualTrigger('');
            setShowManualResForm(null);
            fetchQuestions(selectedTopic.id);
        } catch (err) {
            console.error("Failed to add manual response", err);
        }
    };

    const handleDeleteQuestion = (questionId) => {
        setDeleteData({ id: questionId, type: 'question' });
        setIsDeleteModalOpen(true);
    };

    const handleDeleteResponse = (responseId, questionId) => {
        setDeleteData({ id: responseId, type: 'response', extraId: questionId });
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        try {
            if (deleteData.type === 'topic') {
                await apiClient.deleteKBTopic(deleteData.id);
                if (selectedTopic?.id === deleteData.id) setSelectedTopic(null);
                fetchTopics();
            } else if (deleteData.type === 'question') {
                await apiClient.deleteKBQuestion(deleteData.id);
                fetchQuestions(selectedTopic.id);
            } else if (deleteData.type === 'response') {
                await apiClient.deleteKBResponse(deleteData.id);
                fetchQuestions(selectedTopic.id);
            }
            setIsDeleteModalOpen(false);
        } catch (err) {
            console.error(`Failed to delete ${deleteData.type}`, err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleUpdateQuestion = async (questionId) => {
        if (!editingQuestionText.trim()) return;
        try {
            await apiClient.updateKBQuestion(questionId, editingQuestionText);
            setEditingQuestionId(null);
            fetchQuestions(selectedTopic.id);
        } catch (err) {
            console.error("Failed to update question", err);
        }
    };

    const handleUpdateResponse = async (responseId) => {
        if (!editingInstruction.trim() || !editingTrigger.trim()) return;
        try {
            await apiClient.updateKBResponse(responseId, editingInstruction, editingTrigger);
            setEditingResponseId(null);
            fetchQuestions(selectedTopic.id);
        } catch (err) {
            console.error("Failed to update response", err);
        }
    };

    const handleImproveInstruction = async (currentText, setter, id = null) => {
        if (!currentText.trim()) return;
        setIsImprovingVariation(id || 'manual');
        try {
            const data = await apiClient.improvePrompt(currentText);
            if (data.success) {
                setter(data.improved_prompt);
            }
        } catch (err) {
            showToast("Failed to improve instruction with AI.", "error");
        } finally {
            setIsImprovingVariation(null);
        }
    };

    const toggleQuestionExpansion = (id) => {
        setExpandedQuestions(prev =>
            prev.includes(id) ? prev.filter(qId => qId !== id) : [...prev, id]
        );
    };

    return (
        <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn border-t border-gray-100">
            <div className="w-full max-w-6xl mx-auto space-y-6">
                <PageHeader 
                  title="Smart FAQ Builder"
                  description="Train your AI assistant with context-aware question and answer variations to handle customer queries with high precision."
                />

                <div className="flex gap-8 items-start">
                    {/* Topics Sidebar Card */}
                    <div className="w-[300px] shrink-0 bg-white rounded-[32px] border border-gray-100 shadow-sm flex flex-col overflow-hidden min-h-[500px]">
                        <div className="p-6 border-b border-gray-50 bg-gray-50/30">
                            <div className="flex justify-between items-center mb-1.5">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400">Topics</h3>
                                <button
                                    onClick={() => setShowTopicModal(true)}
                                    className="p-1.5 bg-gray-900 text-white rounded-lg hover:bg-black transition-all shadow-md shadow-gray-200"
                                    title="Create Topic"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                            <p className="text-xs font-bold text-gray-900 tracking-tight">Knowledge Domains</p>
                        </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-1">
                    {topics.length === 0 ? (
                        <div className="p-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                           <HelpCircle size={32} className="mx-auto text-gray-300 mb-2 opacity-30" />
                           <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No Topics Yet</p>
                        </div>
                    ) : topics.map(topic => (
                        <div
                            key={topic.id}
                            onClick={() => setSelectedTopic(topic)}
                            className={`group p-4 rounded-2xl cursor-pointer transition-all flex justify-between items-center ${
                                selectedTopic?.id === topic.id 
                                ? 'bg-gray-900 text-white shadow-xl shadow-gray-200' 
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className={`p-2 rounded-xl transition-colors ${
                                    selectedTopic?.id === topic.id ? 'bg-gray-800' : 'bg-gray-50 group-hover:bg-white'
                                }`}>
                                    <Layers size={16} />
                                </div>
                                <span className="text-sm font-bold truncate tracking-tight">{topic.name}</span>
                            </div>
                            <button
                                onClick={(e) => handleDeleteTopic(e, topic.id)}
                                className={`p-1.5 rounded-lg transition-all ${
                                    selectedTopic?.id === topic.id 
                                    ? 'text-gray-500 hover:text-red-400 hover:bg-gray-800' 
                                    : 'text-gray-300 hover:text-red-500 hover:bg-red-50'
                                }`}
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
                
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
                {selectedTopic ? (
                    <div className="space-y-6">
                        <div className="bg-white border border-gray-100 rounded-[32px] p-6 shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-900 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-gray-200">
                                    <Layers size={20} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">{selectedTopic.name}</h3>
                                    <p className="text-gray-400 text-xs font-medium leading-relaxed mt-0.5">
                                        {selectedTopic.description || `Specialized intelligence repository for ${selectedTopic.name}.`}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Content Tabs */}
                        <div className="flex bg-white p-1.5 rounded-[24px] border border-gray-100 shadow-sm w-fit mb-6">
                            <button
                                onClick={() => setActiveTab('questions')}
                                className={`px-8 py-3 rounded-[18px] text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                                    activeTab === 'questions' 
                                    ? 'bg-gray-900 text-white shadow-xl shadow-gray-200' 
                                    : 'text-gray-400 hover:text-gray-900'
                                }`}
                            >
                                <HelpCircle size={18} />
                                FAQ Repository
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${
                                    activeTab === 'questions' ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-400'
                                }`}>
                                    {questions.length}
                                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('recommendations')}
                                className={`px-8 py-3 rounded-[18px] text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                                    activeTab === 'recommendations' 
                                    ? 'bg-gray-900 text-white shadow-xl shadow-gray-200' 
                                    : 'text-gray-400 hover:text-gray-900'
                                }`}
                            >
                                <BrainCircuit size={18} />
                                AI Idea Lab
                            </button>
                        </div>

                        {activeTab === 'questions' ? (
                            <div className="space-y-8 animate-slideIn">
                                <div className="relative group max-w-2xl">
                                    <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                                    <input
                                        type="text"
                                        placeholder="Search knowledge repository..."
                                        className="w-full pl-14 pr-6 py-4 bg-white border border-gray-100 rounded-[20px] text-xs font-medium focus:ring-4 focus:ring-gray-900/5 focus:border-gray-200 outline-none transition-all shadow-sm"
                                    />
                                </div>

                                <div className="grid gap-4 max-h-[550px] overflow-y-auto pr-2 custom-scrollbar">
                                    {questions.slice((currentPage - 1) * QUESTIONS_PER_PAGE, currentPage * QUESTIONS_PER_PAGE).map(q => (
                                        <div key={q.id} className="bg-white border border-gray-100 rounded-[32px] overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300">
                                            <div
                                                onClick={() => toggleQuestionExpansion(q.id)}
                                                className="px-8 py-6 flex items-center justify-between cursor-pointer group/item"
                                            >
                                                <div className="flex items-center gap-5 flex-1 mr-8">
                                                    <div className={`p-2 rounded-xl transition-all ${expandedQuestions.includes(q.id) ? 'bg-gray-900 text-white rotate-90' : 'bg-gray-50 text-gray-400 group-hover/item:bg-gray-100'}`}>
                                                        <ChevronRight size={20} />
                                                    </div>
                                                    
                                                    {editingQuestionId === q.id ? (
                                                        <div className="flex items-start gap-4 flex-1">
                                                            <textarea
                                                                value={editingQuestionText}
                                                                onChange={(e) => setEditingQuestionText(e.target.value)}
                                                                autoFocus
                                                                className="w-full p-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-bold text-xs uppercase tracking-widest leading-relaxed resize-none h-24 transition-all"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <div className="flex flex-col gap-2 pt-2">
                                                                <button onClick={(e) => { e.stopPropagation(); handleUpdateQuestion(q.id); }} className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100"><Check size={20} /></button>
                                                                <button onClick={(e) => { e.stopPropagation(); setEditingQuestionId(null); }} className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100"><X size={20} /></button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-gray-900 leading-tight mb-1.5">{q.text}</span>
                                                            <div className="flex items-center gap-3">
                                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-50 text-gray-400 border border-gray-100">
                                                                    {q.responses?.length || 0} variations
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingQuestionId(q.id);
                                                            setEditingQuestionText(q.text);
                                                            if (!expandedQuestions.includes(q.id)) toggleQuestionExpansion(q.id);
                                                        }}
                                                        className="p-3 text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-2xl transition-all"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(q.id); }}
                                                        className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </div>

                                            {expandedQuestions.includes(q.id) && (
                                                <div className="px-8 pb-8 pt-2 bg-gray-50/30 border-t border-gray-50 animate-fadeIn ml-16 mr-8 mb-8 rounded-[24px]">
                                                    <div className="space-y-4">
                                                        {q.responses?.map(resp => (
                                                            <div key={resp.id} className="bg-white border border-gray-100 rounded-[28px] p-8 shadow-sm group/resp relative">
                                                                <div className="flex justify-between items-start gap-8">
                                                                    <div className="flex-1">
                                                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                                            <Info size={12} className="text-blue-500" />
                                                                            Variation Logic
                                                                        </div>
                                                                        {editingResponseId === resp.id ? (
                                                                            <div className="space-y-4">
                                                                                <div className="relative">
                                                                                    <textarea
                                                                                        value={editingInstruction}
                                                                                        onChange={(e) => setEditingInstruction(e.target.value)}
                                                                                        className="w-full p-5 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-medium text-base leading-relaxed h-32"
                                                                                    />
                                                                                    <button
                                                                                        onClick={() => handleImproveInstruction(editingInstruction, setEditingInstruction, resp.id)}
                                                                                        disabled={isImprovingVariation === resp.id}
                                                                                        className="absolute right-4 bottom-4 p-2 bg-white text-gray-900 rounded-xl shadow-lg border border-gray-100 hover:scale-105 transition-all disabled:opacity-50"
                                                                                    >
                                                                                        <Sparkles size={18} className={isImprovingVariation === resp.id ? "animate-spin" : ""} />
                                                                                    </button>
                                                                                </div>
                                                                                <div className="space-y-2">
                                                                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Trigger Condition</label>
                                                                                    <input
                                                                                        value={editingTrigger}
                                                                                        onChange={(e) => setEditingTrigger(e.target.value)}
                                                                                        className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-bold text-sm"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="space-y-4">
                                                                                <div className="bg-blue-50/30 border border-blue-100/50 p-4 rounded-xl">
                                                                                    <p className="text-gray-900 font-medium text-[11px] leading-relaxed">"{resp.instruction_text}"</p>
                                                                                </div>
                                                                                <div className="flex items-center gap-3">
                                                                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 px-2 py-1 rounded-lg">Trigger</span>
                                                                                    <p className="text-[11px] font-medium text-gray-600 truncate">{resp.trigger_condition}</p>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        {editingResponseId === resp.id ? (
                                                                            <>
                                                                                <button onClick={() => handleUpdateResponse(resp.id)} className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100"><Check size={18} /></button>
                                                                                <button onClick={() => setEditingResponseId(null)} className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100"><X size={18} /></button>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <button onClick={() => { setEditingResponseId(resp.id); setEditingInstruction(resp.instruction_text); setEditingTrigger(resp.trigger_condition); }} className="p-2 text-gray-300 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all"><Edit2 size={16} /></button>
                                                                                <button onClick={() => handleDeleteResponse(resp.id, q.id)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 size={16} /></button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {showManualResForm === q.id ? (
                                                            <div className="bg-white border border-gray-900 rounded-[28px] p-8 shadow-2xl animate-scaleIn mt-4">
                                                                <div className="flex items-center gap-3 mb-6">
                                                                    <div className="w-8 h-8 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                                                                        <Plus size={16} />
                                                                    </div>
                                                                    <h4 className="font-bold text-gray-900">Add New Variation</h4>
                                                                </div>
                                                                <div className="space-y-6">
                                                                    <div className="space-y-2">
                                                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Instruction</label>
                                                                        <div className="relative">
                                                                            <textarea
                                                                                value={manualInstruction}
                                                                                onChange={(e) => setManualInstruction(e.target.value)}
                                                                                placeholder="Enter specific instruction..."
                                                                                className="w-full p-5 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-medium text-sm h-32 resize-none"
                                                                            />
                                                                            <button
                                                                                onClick={() => handleImproveInstruction(manualInstruction, setManualInstruction, 'manual')}
                                                                                disabled={isImprovingVariation === 'manual'}
                                                                                className="absolute right-4 bottom-4 p-2 bg-white text-gray-900 rounded-xl shadow-lg border border-gray-100 hover:scale-105 transition-all disabled:opacity-50"
                                                                            >
                                                                                <Sparkles size={18} className={isImprovingVariation === 'manual' ? "animate-spin" : ""} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Trigger Condition</label>
                                                                        <input
                                                                            value={manualTrigger}
                                                                            onChange={(e) => setManualTrigger(e.target.value)}
                                                                            placeholder="e.g. When asking about specific status"
                                                                            className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-bold text-sm"
                                                                        />
                                                                    </div>
                                                                    <div className="flex gap-3 pt-2">
                                                                        <button onClick={() => setShowManualResForm(null)} className="flex-1 px-6 py-4 text-gray-400 hover:text-gray-900 font-bold text-sm rounded-xl">Discard</button>
                                                                        <button onClick={() => handleAddManualResponse(q.id)} className="flex-1 px-8 py-4 bg-gray-900 text-white font-bold text-sm rounded-2xl hover:bg-gray-800 transition-all shadow-xl shadow-gray-200">Save Variation</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setShowManualResForm(q.id)}
                                                                className="w-full py-4 rounded-[24px] border-2 border-dashed border-gray-100 text-gray-400 font-bold text-xs uppercase tracking-widest hover:border-gray-900 hover:text-gray-900 transition-all mt-4 bg-white/50"
                                                            >
                                                                + Add Manual Variation
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {questions.length === 0 && !loading && (
                                        <div className="p-20 text-center bg-white rounded-[40px] border border-gray-100 shadow-sm">
                                            <div className="w-20 h-20 bg-gray-50 rounded-[28px] flex items-center justify-center mx-auto mb-6 border border-gray-100">
                                                <BookOpen size={32} className="text-gray-300" />
                                            </div>
                                            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2">Knowledge Base Empty</h3>
                                            <p className="text-gray-400 max-w-sm mx-auto mb-8 font-medium">This topic hasn't been trained yet. Use the AI Idea Lab to brainstorm questions or add them manually.</p>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination Controls */}
                                {Math.ceil(questions.length / QUESTIONS_PER_PAGE) > 1 && (
                                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-50">
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest px-2">
                                            Page {currentPage} of {Math.ceil(questions.length / QUESTIONS_PER_PAGE)}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                            >
                                                <ChevronLeft size={14} />
                                            </button>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(questions.length / QUESTIONS_PER_PAGE)))}
                                                disabled={currentPage === Math.ceil(questions.length / QUESTIONS_PER_PAGE)}
                                                className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                            >
                                                <ChevronRight size={14} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-12 animate-slideIn">
                                <div className="bg-white border border-gray-100 rounded-[40px] p-10 shadow-xl shadow-gray-100/50 relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-32 -mt-32 opacity-50 blur-3xl group-hover:bg-blue-100 transition-colors duration-700"></div>
                                    
                                    <div className="relative z-10">
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="w-14 h-14 bg-gray-900 rounded-[20px] flex items-center justify-center text-white shadow-lg shadow-gray-200">
                                                <Sparkles size={24} />
                                            </div>
                                            <div>
                                                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 tracking-tight">AI Idea Lab</h3>
                                                <p className="text-gray-400 font-medium text-sm">Brainstorm context-aware questions from your data</p>
                                            </div>
                                        </div>

                                        <p className="text-gray-500 font-medium text-base mb-8 max-w-xl leading-relaxed">
                                            Enter a seed question or topic. Our AI will brainstorm <span className="text-gray-900 font-bold">3 distinct variations</span> a user might ask, then help you craft the perfect logic for each.
                                        </p>

                                        <div className="flex gap-4">
                                            <div className="flex-1 relative group/input">
                                                <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within/input:text-gray-900 transition-colors" />
                                                <input
                                                    type="text"
                                                    value={newQuestion}
                                                    onChange={(e) => setNewQuestion(e.target.value)}
                                                    placeholder="e.g. Order Tracking or Return Policy"
                                                    className="w-full pl-14 pr-6 py-5 bg-gray-50 border-0 rounded-[24px] text-lg focus:ring-4 focus:ring-gray-900/5 focus:bg-white outline-none transition-all"
                                                />
                                            </div>
                                            <button
                                                onClick={handleGenerateVariations}
                                                disabled={isGenerating || !newQuestion.trim()}
                                                className="px-8 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all flex items-center gap-2 shadow-lg shadow-gray-200 disabled:opacity-50 active:scale-95 mx-auto"
                                            >
                                                {isGenerating ? <Sparkles size={14} className="animate-spin" /> : <Zap size={14} />}
                                                Generate
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {recommendedVariations.length > 0 && (
                                    <div className="space-y-8 animate-slideUp">
                                        <div className="flex items-center gap-3">
                                            <div className="w-2 h-8 bg-gray-900 rounded-full"></div>
                                            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-900 uppercase tracking-widest">AI Brainstorm Results</h4>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 gap-6">
                                            {recommendedVariations.map((v, i) => (
                                                <div key={i} className="bg-white border border-gray-100 rounded-[40px] overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-100/30 transition-all duration-500">
                                                    <div className="p-10">
                                                        <div className="mb-8">
                                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3 block">Question Variation (AI-Generated)</label>
                                                            <input
                                                                type="text"
                                                                value={v.question_text}
                                                                onChange={(e) => handleUpdateVariationInput(i, 'question_text', e.target.value)}
                                                                className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-gray-900 outline-none transition-all"
                                                            />
                                                        </div>

                                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                            <div className="space-y-3">
                                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">Variation Logic</label>
                                                                <div className="relative group/area">
                                                                    <textarea
                                                                        value={v.instruction_text}
                                                                        onChange={(e) => handleUpdateVariationInput(i, 'instruction_text', e.target.value)}
                                                                        placeholder="How should the AI respond to this variant?"
                                                                        className="w-full p-4 bg-gray-50 border-0 rounded-xl text-gray-900 font-bold text-xs leading-relaxed h-32 focus:ring-2 focus:ring-gray-900 outline-none resize-none transition-all group-hover/area:bg-white"
                                                                    />
                                                                    <button
                                                                        onClick={() => handleImproveInstruction(v.instruction_text, (val) => handleUpdateVariationInput(i, 'instruction_text', val), `gen-${i}`)}
                                                                        disabled={isImprovingVariation === `gen-${i}`}
                                                                        className="absolute right-4 bottom-4 p-2 bg-white text-gray-900 rounded-xl shadow-lg border border-gray-100 hover:scale-110 transition-all disabled:opacity-50"
                                                                    >
                                                                        <Sparkles size={20} className={isImprovingVariation === `gen-${i}` ? "animate-spin" : ""} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="space-y-3">
                                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">Trigger Condition</label>
                                                                 <div className="relative">
                                                                    <input
                                                                        type="text"
                                                                        value={v.trigger_condition}
                                                                        onChange={(e) => handleUpdateVariationInput(i, 'trigger_condition', e.target.value)}
                                                                        placeholder="e.g. When the specific order status is requested"
                                                                        className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl text-gray-900 font-bold text-xs focus:ring-2 focus:ring-gray-900 outline-none transition-all leading-normal"
                                                                    />
                                                                    <div className="absolute right-5 top-1/2 -translate-y-1/2 text-blue-500">
                                                                        <Zap size={16} />
                                                                    </div>
                                                                </div>
                                                                <div className="p-6 bg-blue-50 rounded-[28px] border border-blue-100/50 mt-4 flex items-start gap-3">
                                                                    <Info size={16} className="text-blue-500 mt-1 shrink-0" />
                                                                    <p className="text-xs font-medium text-blue-700/80 leading-relaxed">
                                                                        "Triggers ensure the AI only uses this variation when relevant, preventing hallucination."
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex bg-gray-50/50 border-t border-gray-100 p-2 gap-2">
                                                        <button
                                                            onClick={() => handleApproveVariation(v, i)}
                                                            className="flex-1 py-3 bg-white hover:bg-blue-500 hover:text-white text-blue-600 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group/save border border-gray-100 shadow-sm"
                                                        >
                                                            <Check size={16} className="group-hover/save:scale-110 transition-transform" />
                                                            Approve & Train
                                                        </button>
                                                        <button
                                                            onClick={() => setRecommendedVariations(prev => prev.filter((_, idx) => idx !== i))}
                                                            className="flex-1 py-3 bg-white hover:bg-red-500 hover:text-white text-red-500 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group/reject border border-gray-100 shadow-sm"
                                                        >
                                                            <Trash2 size={16} className="group-hover/reject:scale-110 transition-transform" />
                                                            Discard
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {isGenerating && (
                                    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                                        <div className="w-16 h-16 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin mb-6"></div>
                                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs tracking-[0.3em]">AI Brainstorming...</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[32px] border border-gray-100 shadow-sm animate-fadeIn">
                            <div className="w-16 h-16 bg-gray-900 rounded-[20px] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-gray-200 text-white">
                                <BookOpen size={28} />
                            </div>
                            <h2 className="text-xl font-extrabold text-gray-900 mb-2 tracking-tight">Select a Topic</h2>
                            <p className="text-gray-400 text-xs font-medium leading-relaxed text-center max-w-xs">
                                Choose a knowledge domain from the sidebar or build a new one to start training your assistant.
                            </p>
                            <button
                                onClick={() => setShowTopicModal(true)}
                                className="mt-6 px-6 py-3 bg-gray-900 text-white rounded-xl font-bold text-[11px] uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-gray-200 active:scale-95"
                            >
                                Build New Topic
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </div>

            {/* Design Modal */}
            {showTopicModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[9999] p-6 animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl animate-scaleIn border border-gray-100 relative overflow-hidden">
                        
                        <div className="flex justify-between items-center mb-10">
                            <div>
                                <h3 className="text-2xl font-black text-gray-900 tracking-tight">New Knowledge Domain</h3>
                                <p className="text-gray-400 text-sm font-medium mt-1 uppercase tracking-widest">Create specialized intelligence</p>
                            </div>
                            <button 
                                onClick={() => setShowTopicModal(false)}
                                className="p-3 bg-gray-50 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-2xl transition-all"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">Domain Name</label>
                                <input
                                    value={topicName}
                                    onChange={(e) => setTopicName(e.target.value)}
                                    placeholder="e.g. Refund Policy or Product FAQ"
                                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl text-sm font-bold text-gray-900 focus:ring-4 focus:ring-gray-900/5 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                                />
                            </div>
                            
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">Objective (Optional)</label>
                                <textarea
                                    value={topicDesc}
                                    onChange={(e) => setTopicDesc(e.target.value)}
                                    placeholder="Describe the scope for the AI..."
                                    className="w-full p-6 bg-gray-50 border-0 rounded-[28px] text-gray-900 font-medium text-base leading-relaxed h-32 focus:ring-4 focus:ring-gray-900/5 focus:bg-white outline-none resize-none transition-all"
                                />
                            </div>

                            <div className="flex justify-center mt-4">
                                <button
                                    onClick={handleCreateTopic}
                                    disabled={!topicName.trim()}
                                    className="px-10 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-50"
                                >
                                    Create Intelligence Domain
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal 
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                isLoading={isDeleting}
                title={`Delete ${deleteData.type === 'topic' ? 'Intelligence Hub' : deleteData.type === 'question' ? 'Neural Link' : 'Synaptic Response'}?`}
                message={`This action will permanently purge this ${deleteData.type} from the neural repository. Are you prepared to proceed?`}
            />
            </div>
        </div>
    );
};

export default KnowledgeBase;
